import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Eye, Heart, Share2, BookOpen, Users, MapPin } from 'lucide-react';
import { apiRequest } from '../lib/apiClient';
import { toast } from 'sonner';

interface StoryPreviewData {
  title: string;
  description: string;
  author: {
    name: string;
    avatar: string;
  };
  previewContent: {
    bookMetadata: any;
    characters: any[];
    locations: any[];
    chapters: any[];
    totalChapters: number;
    totalWordCount: number;
  };
  viewCount: number;
  shareCount: number;
  createdAt: string;
}

export const StoryPreview: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const [story, setStory] = useState<StoryPreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (shareId) {
      fetchStoryPreview(shareId);
    }
  }, [shareId]);

  const fetchStoryPreview = async (shareId: string) => {
    try {
      const response = await apiRequest(`/api/sharing/preview/${shareId}`, 'GET');
      // FIX: The response from apiRequest is a Response object and needs to be parsed as JSON.
      const data = await response.json();
      setStory(data);
    } catch (error: any) {
      console.error('Error fetching story preview:', error);
      if (error.status === 404) {
        setError('Story not found or no longer available');
      } else if (error.status === 410) {
        setError('This story preview has expired');
      } else {
        setError('Failed to load story preview');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignupRedirect = () => {
    // Track conversion
    if (shareId) {
      apiRequest('/api/sharing/track', 'POST', {
        shareId,
        actionType: 'signup',
        shareChannel: 'preview_page'
      }).catch(console.error);
    }
    
    navigate(`/auth?ref=${shareId}`);
  };

  const handleShare = async () => {
    if (!shareId) return;
    
    try {
      const shareUrl = window.location.href;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Story link copied to clipboard!');
      
      // Track share action
      await apiRequest('/api/sharing/track', 'POST', {
        shareId,
        actionType: 'share',
        shareChannel: 'preview_share'
      });
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="h-64 bg-gray-200 rounded"></div>
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <h2 className="text-xl font-semibold mb-2">Story Not Available</h2>
            <p className="text-muted-foreground mb-4">{error || 'Story not found'}</p>
            <Button onClick={() => navigate('/')} variant="outline">
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="text-2xl font-bold text-blue-600"
              >
                StoryLikho
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" onClick={handleShare}>
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              <Button onClick={handleSignupRedirect}>
                Start Writing
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Story Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <Avatar className="w-12 h-12">
              <AvatarImage src={story.author.avatar} />
              <AvatarFallback>
                {story.author.name.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-3xl font-bold">{story.title}</h1>
              <p className="text-muted-foreground">by {story.author.name}</p>
            </div>
          </div>
          
          {story.description && (
            <p className="text-lg text-muted-foreground mb-4">{story.description}</p>
          )}
          
          <div className="flex items-center space-x-6 text-sm text-muted-foreground">
            <div className="flex items-center">
              <Eye className="w-4 h-4 mr-1" />
              {story.viewCount.toLocaleString()} views
            </div>
            <div className="flex items-center">
              <Share2 className="w-4 h-4 mr-1" />
              {story.shareCount} shares
            </div>
            <div className="flex items-center">
              <BookOpen className="w-4 h-4 mr-1" />
              {story.previewContent.totalChapters} chapters
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Book Metadata */}
            {story.previewContent.bookMetadata && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <BookOpen className="w-5 h-5 mr-2" />
                    Story Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    {story.previewContent.bookMetadata.genre && (
                      <div>
                        <h4 className="font-medium text-sm text-muted-foreground">Genre</h4>
                        <Badge variant="secondary">{story.previewContent.bookMetadata.genre}</Badge>
                      </div>
                    )}
                    {story.previewContent.bookMetadata.tone && (
                      <div>
                        <h4 className="font-medium text-sm text-muted-foreground">Tone</h4>
                        <Badge variant="outline">{story.previewContent.bookMetadata.tone}</Badge>
                      </div>
                    )}
                    <div>
                      <h4 className="font-medium text-sm text-muted-foreground">Word Count</h4>
                      <p>{story.previewContent.totalWordCount?.toLocaleString() || 'TBD'}</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-sm text-muted-foreground">Chapters</h4>
                      <p>{story.previewContent.totalChapters}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Chapter Previews */}
            <Card>
              <CardHeader>
                <CardTitle>Chapter Previews</CardTitle>
                <p className="text-sm text-muted-foreground">
                  First {story.previewContent.chapters.length} chapters available
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {story.previewContent.chapters.map((chapter, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <h3 className="font-semibold mb-2">
                      Chapter {index + 1}: {chapter.title}
                    </h3>
                    <div className="text-sm text-muted-foreground prose max-w-none">
                      {chapter.content && (
                        <div dangerouslySetInnerHTML={{ __html: chapter.content }} />
                      )}
                    </div>
                    {chapter.wordCount && (
                      <p className="text-xs text-muted-foreground mt-2">
                        {chapter.wordCount.toLocaleString()} words
                      </p>
                    )}
                  </div>
                ))}
                
                <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                  <h3 className="text-lg font-semibold mb-2">Want to read more?</h3>
                  <p className="text-muted-foreground mb-4">
                    Sign up to access the full story and create your own!
                  </p>
                  <Button onClick={handleSignupRedirect} size="lg">
                    Continue Reading
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Characters */}
            {story.previewContent.characters.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Users className="w-5 h-5 mr-2" />
                    Characters
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {story.previewContent.characters.map((character, index) => (
                    <div key={index} className="border-b pb-2 last:border-b-0">
                      <h4 className="font-medium">{character.name}</h4>
                      <p className="text-sm text-muted-foreground">{character.description}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Locations */}
            {story.previewContent.locations.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <MapPin className="w-5 h-5 mr-2" />
                    Locations
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {story.previewContent.locations.map((location, index) => (
                    <div key={index} className="border-b pb-2 last:border-b-0">
                      <h4 className="font-medium">{location.name}</h4>
                      <p className="text-sm text-muted-foreground">{location.description}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Call to Action */}
            <Card className="bg-gradient-to-b from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Start Your Story Journey</h3>
                <ul className="text-sm space-y-1 mb-4">
                  <li>✓ AI-powered story generation</li>
                  <li>✓ Character & world building tools</li>
                  <li>✓ Professional book export</li>
                  <li>✓ Cover art generation</li>
                </ul>
                <Button onClick={handleSignupRedirect} className="w-full">
                  Join Free Today
                </Button>
                <p className="text-xs text-center text-muted-foreground mt-2">
                  Start with 10,000 free credits
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryPreview;